import { useCallback, useMemo, useRef, type ReactNode } from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
import {
    isCloseFromDescendantLevel,
    usePickerChainOrThrow,
    usePickerLevelOrThrow,
} from "./PickerChainContext";
import { usePickerQuery, usePickerRoot } from "./PickerContext";
import { PickerPopup } from "./PickerPopup";
import { PickerRootListContent, PickerRootShell } from "./PickerRootShell";
import { pruneItemsByQuery, pruneSectionsByQuery } from "./PickerTreeQuery";
import { usePickerListRoot } from "./usePickerListRoot";
import { toValueList } from "./usePickerSelection";
import type {
    PickerFlyoutOptions,
    PickerItemData,
    PickerItemValue,
    PickerNested,
    PickerPopupProps,
    PickerSectionData,
} from "./Picker.types";

export interface PickerFlyoutLevelProps {
    /** The row the level is anchored to and returns focus to. */
    anchor: HTMLElement;
    options: PickerFlyoutOptions;
    items?: ReadonlyArray<PickerItemData<PickerItemValue>>;
    sections?: ReadonlyArray<PickerSectionData<PickerItemValue>>;
    nested: PickerNested;
    /** Keyboard close (Escape, ArrowLeft): the level closes and its row takes focus. */
    onCloseToRow: () => void;
    onPointerEnter: PickerPopupProps["onPointerEnter"];
    onPointerLeave: PickerPopupProps["onPointerLeave"];
    /** Opened by the top query (`flyoutAutoOpen`), so the level must not take focus. */
    isAutoOpened: boolean;
    children?: ReactNode;
}

/**
 * One cascading level: its own Base UI root (Combobox when searchable,
 * otherwise the chain's kind), open while mounted, positioned against the
 * parent row. Leaf presses commit through the chain to the top root and
 * close every level unless the chain is `multiple`; Escape closes this level
 * only and returns focus to its row.
 */
export const PickerFlyoutLevel = ({
    anchor,
    options,
    items,
    sections,
    nested,
    onCloseToRow,
    onPointerEnter,
    onPointerLeave,
    isAutoOpened,
    children,
}: PickerFlyoutLevelProps) => {
    const parentRoot = usePickerRoot();
    const parentQuery = usePickerQuery();
    const chain = usePickerChainOrThrow();
    const parentLevel = usePickerLevelOrThrow();
    const depth = parentLevel.depth + 1;
    // `path` scoping: the parent level's filtering text (itself already
    // narrowed by its ancestors) prunes this level to the rows that match or
    // lead to a match; the level's own search narrows further.
    const ancestorQuery =
        chain.flyoutQueryScope === "path" ? parentQuery.filterQuery : "";
    const scopedItems = useMemo(
        () =>
            items === undefined
                ? undefined
                : pruneItemsByQuery(items, ancestorQuery),
        [items, ancestorQuery],
    );
    const scopedSections = useMemo(
        () =>
            sections === undefined
                ? undefined
                : pruneSectionsByQuery(sections, ancestorQuery),
        [sections, ancestorQuery],
    );
    const isSearchable = options.searchable === true;
    const kind = isSearchable ? "combobox" : chain.kind;
    // Stable across the ancestor's keystrokes, so the level's root context
    // (and its memoised rows) does not refresh while typing above it.
    const { commitValues, closeAll, isMultiple } = chain;
    const handleLevelValueChange = useCallback(
        (next: PickerItemValue[] | PickerItemValue | null) => {
            commitValues(toValueList(next));
            if (!isMultiple) {
                closeAll();
            }
        },
        [commitValues, closeAll, isMultiple],
    );

    const level = usePickerListRoot<PickerItemValue>({
        kind,
        isMultiple: chain.isMultiple,
        value: chain.isMultiple
            ? chain.selectedValues
            : (chain.selectedValues[0] ?? null),
        defaultValue: null,
        onValueChange: handleLevelValueChange,
        items: scopedItems,
        sections: scopedSections,
        nested,
        selectableParents: parentRoot.isParentSelectable,
        parentSelection: parentRoot.parentSelection,
        inheritedIndicatedDescendants: parentRoot.indicatedDescendants,
        chain,
        depth,
        topQuery: parentQuery.topQuery,
        shouldFilter: kind === "combobox",
        hasPopupSearch: isSearchable,
        open: true,
        emptyBehavior: options.emptyBehavior,
        status: options.status,
        renderFooter: options.renderFooter,
        disabled: false,
        isInvalid: false,
        classNames: parentRoot.classNames,
    });

    const handleOpenChange = (
        nextOpen: boolean,
        details: { reason: string; event: Event; cancel: () => void },
    ) => {
        if (nextOpen) {
            return;
        }
        if (details.reason === "escape-key") {
            onCloseToRow();
            return;
        }
        // Presses and focus moves never close a level on their own: the
        // row's hover logic and the top root's close own that. A request
        // raised inside a deeper level is cancelled so Base UI stays quiet.
        if (
            isCloseFromDescendantLevel({
                reason: details.reason,
                event: details.event,
                chainId: chain.chainId,
                depth,
            })
        ) {
            details.cancel();
        }
    };

    // A level opened by the user takes focus: its search when it has one,
    // otherwise the popup itself. An auto-opened level leaves focus where
    // the user is typing.
    const searchAutoFocusRef = useRef<boolean | null>(!isAutoOpened);
    const levelContextValue = level.contextValue;
    const contextValue = useMemo(
        () => ({
            ...levelContextValue,
            triggerAnchor: anchor,
            searchAutoFocusRef,
        }),
        [levelContextValue, anchor],
    );
    const closeLevel = useCallback(() => onCloseToRow(), [onCloseToRow]);
    const popupProps = useMemo<Partial<PickerPopupProps>>(
        () => ({ matchTriggerWidth: false, onPointerEnter, onPointerLeave }),
        [onPointerEnter, onPointerLeave],
    );
    const content =
        items !== undefined || sections !== undefined ? (
            <PickerRootListContent
                root={level}
                hasPopupSearch={isSearchable}
                searchAutoFocus={!isAutoOpened}
                isInline={false}
                searchPlaceholder={options.searchPlaceholder}
                status={options.status}
                loadingLabel={options.loadingLabel}
                errorLabel={options.errorLabel}
                emptyLabel={options.emptyLabel}
                emptyBehavior={options.emptyBehavior}
                popupProps={popupProps}
            />
        ) : (
            <PickerPopup {...popupProps}>{children}</PickerPopup>
        );

    return (
        <PickerRootShell
            root={level}
            contextValue={contextValue}
            closeLevel={closeLevel}
        >
            {kind === "combobox" ? (
                <ComboboxBase.Root<PickerItemValue, boolean>
                    multiple={chain.isMultiple}
                    value={level.baseValue}
                    onValueChange={level.handleBaseValueChange}
                    open={level.isOpen}
                    onOpenChange={handleOpenChange}
                    inputValue={level.query}
                    onInputValueChange={(nextQuery, details) =>
                        level.handleInputValueChange(nextQuery, details.reason)
                    }
                    autoHighlight={isSearchable ? chain.isAutoHighlight : true}
                    modal={false}
                >
                    {content}
                </ComboboxBase.Root>
            ) : (
                <SelectBase.Root<PickerItemValue, boolean>
                    multiple={chain.isMultiple}
                    value={level.baseValue}
                    onValueChange={level.handleBaseValueChange}
                    open={level.isOpen}
                    onOpenChange={handleOpenChange}
                    modal={false}
                >
                    {content}
                </SelectBase.Root>
            )}
        </PickerRootShell>
    );
};
